import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import { useIdle } from '@vueuse/core'
import { getApiUrl } from '@/api'
import { useAuthStore } from './auth'
import type { WatchRole } from '@/schemas'
import type { ControllerOut } from '@/schemas/schemas.ts'
import { useToastStore } from '@/stores/toast.ts'

export type SessionStatus =
  | 'created'
  | 'ready'
  | 'failsafe'
  | 'countdown'
  | 'paused'
  | 'success'
  | 'fail'
  | 'aborted'
  | 'closed'

export type ControllerCommand = 'start' | 'pause' | 'restart' | 'reset' | 'punish'
export type OwnerCommand = 'open' | 'abort' | 'trigger' | 'close' | 'set_length'
export type Command = ControllerCommand | OwnerCommand

export type SessionID = number & { __special_branding__: 'session_id' }
export type SessionUUID = string & { __special_branding__: 'session_uuid' }

/**
 * special profile picture value `""` indicating no profile picture server side,
 * as opposed to `null` which means hidden-due-to-anon.
 *  */
export type NonAnonNull = '' & { __special_branding__: 'non-anon-null' }
export const NON_ANON_NULL: NonAnonNull = '' as NonAnonNull

export interface PayloadFail {
  punishment_text: string
}

export type AllowedCmdsBase = {
  [key in WatchRole]: Command[]
}
export interface AllowedCmds extends AllowedCmdsBase {
  owner: OwnerCommand[]
  controller: ControllerCommand[]
}

export interface SessionStateOut {
  status: SessionStatus
  countdown_seconds: number
  user: string
  owner: ControllerOut
  owner_connected: ControllerOut | null
  time_left?: number | null
  started_at?: string | null
  finished_at?: string | null
  punishment_text?: string | null
  controllers?: ControllerOut[]
  allowed_cmds: AllowedCmds
  fail_reason?: 'timeout' | 'invalid' | null
  max_wait_seconds: number | null
  max_wait_left: number | null
  max_wait_time: string | null
  random_wait_seconds: number | null
  random_wait_left: number | null
  random_wait_time: string | null
}

export interface WsStateMessage {
  type: 'state'
  session_id: SessionID
  state: SessionStateOut
}

export interface WsErrorMessage {
  type: 'error'
  error: string
}

export interface WsCommandAckMessage {
  type: 'ack'
  session_id: SessionID
  cmd: Command
  success: boolean
  message: string | null
}

export type WsServerMessage = WsStateMessage | WsErrorMessage | WsCommandAckMessage

// Activity tracking interface
interface ActivityTracker {
  isActive: boolean
  lastActiveTime: number
}

export const canInSession = (
  session: { allowed_cmds?: AllowedCmds } | null | undefined,
  role: WatchRole,
  action: Command,
) => ((session?.allowed_cmds?.[role] ?? []) as Command[]).includes(action) ?? false

export type WebSocketSession = {
  sessionId: SessionID
  role: WatchRole
  socket: WebSocket | null
  state: SessionStateOut | null
  connected: boolean
  reconnectAttempts: number
  activityTracker: ActivityTracker | null
}

export const useWsStore = defineStore('ws', () => {
  const auth = useAuthStore()
  const toast = useToastStore()

  const sessions = ref(new Map<SessionID, WebSocketSession>())

  const watchedSessions = computed(() => {
    return new Set<SessionID>(
      Array.from(sessions.value.values())
        .filter((s) => s.activityTracker !== null)
        .map((s) => s.sessionId),
    )
  })
  const countdownSession = computed<WebSocketSession | null>(() => {
    for (const s of sessions.value.values()) {
      if (s.role === 'owner' && s.state?.status === 'countdown') {
        return s
      }
    }
    return null
  })
  const isCountdownConnected = computed<boolean | undefined>(() => {
    return countdownSession.value?.connected
  })

  // const currentCountdownSessionId = ref<SessionID | null>(null);
  // const currentControllerSessionId = ref<SessionID | null>(null);

  const maxReconnectDelay = 8000
  const debugLog = ref<string[]>([])

  // Activity tracking state
  const isWindowFocused = ref(true)
  const isOnline = ref(navigator.onLine)

  // Initialize idle detection (5 minutes)
  const { idle: isIdle } = useIdle(5 * 60 * 1000) // 5 minutes

  function log(line: string) {
    const ts = new Date().toISOString().slice(11, 23)
    debugLog.value.push(`[${ts}] ${line}`)
    console.log('[WS] ' + line)
    if (debugLog.value.length > 300) debugLog.value.shift()
  }

  function getSession(sessionId: SessionID | null): WebSocketSession | null {
    if (sessionId === null) {
      return null
    }
    return sessions.value.get(sessionId) ?? null
  }

  function getState(sessionId: SessionID): SessionStateOut | null {
    return getSession(sessionId)?.state ?? null
  }

  function isConnected(sessionId: SessionID): boolean {
    return getSession(sessionId)?.connected ?? false
  }

  // Consolidated activity check
  function isUserActive(): boolean {
    return isWindowFocused.value && !isIdle.value && isOnline.value
  }

  function startSession(sessionId: SessionID, role: WatchRole) {
    if (sessions.value.has(sessionId)) {
      stopSession(sessionId)
    }

    sessions.value.set(
      sessionId,
      reactive({
        sessionId,
        role,
        socket: null,
        state: null,
        connected: false,
        reconnectAttempts: 0,
        activityTracker: null,
      }),
    )

    ensureConnected(sessionId)
  }

  function stopSession(sessionId: SessionID) {
    log(`stop session (${sessionId}) is not implemented yet`)
    //throw Error("not implemented" + sessionId);
  }

  // Update activity state for a session
  function updateSessionActivity(sessionId: SessionID, forceState?: boolean) {
    const session = getSession(sessionId)
    if (!session) {
      log(`Cannot update activity: no session for ID ${sessionId}`)
      return
    }
    const tracker = getSession(sessionId)?.activityTracker
    if (!tracker) {
      log(`No activity tracker for session ${sessionId}.`)
      return
    }

    const newActiveState = forceState !== undefined ? forceState : isUserActive()
    const wasActive = tracker.isActive

    if (newActiveState !== wasActive) {
      tracker.isActive = newActiveState
      tracker.lastActiveTime = Date.now()

      log(`Session ${sessionId} activity changed: ${wasActive} → ${newActiveState}`)

      if (newActiveState) {
        // User became active - watch the session
        _sendWatchMessage(sessionId, session.role)
      } else {
        // User became inactive - unwatch the session
        _sendUnwatchMessage(sessionId)
      }
    }
  }

  // Send watch message to server
  function _send(sessionId: SessionID, payload: object) {
    const socket = getSession(sessionId)?.socket
    if (!socket) {
      log(`Send on  ${sessionId}: Cannot send watch message: no socket for session.`)
      return
    }
    if (socket.readyState !== WebSocket.OPEN) {
      log(
        `Send on  ${sessionId}: Cannot send watch message: socket not OPEN, but instead is in state ${socket.readyState}.`,
      )
      return
    }
    socket.send(JSON.stringify(payload))
  }
  function _sendWatchMessage(sessionId: SessionID, role: WatchRole) {
    _send(sessionId, {
      type: 'watch',
      session_id: sessionId,
      role,
    })
    log(`=> watch session ${sessionId} as ${role}`)
  }

  // Send unwatch message to server
  function _sendUnwatchMessage(sessionId: SessionID) {
    _send(sessionId, {
      type: 'unwatch',
      session_id: sessionId,
    })
    log(`=> unwatch session ${sessionId}`)
  }

  // Update all tracked sessions' activity
  function updateAllSessionsActivity(forceState?: boolean) {
    for (const sessionId of watchedSessions.value) {
      if (!getSession(sessionId)?.activityTracker) {
        continue
      }
      updateSessionActivity(sessionId, forceState)
    }
  }

  function _buildUrl(sessionId: SessionID): string | null {
    const params = new URLSearchParams()
    if (auth.token) params.set('token', auth.token)
    // Use the role for this session if we're watching it
    const role: WatchRole | undefined = getSession(sessionId)?.role
    if (role === undefined) {
      log(`Cannot build WS URL: no role for session ${sessionId}`)
      return null
    }
    params.set('role', role)
    // No extra /api for the proxy needed here.
    return `${getApiUrl('ws')}/ws/sessions/${sessionId}?${params.toString()}`
  }

  function _setupSocket(ws: WebSocket, sessionId: SessionID) {
    const session = getSession(sessionId)
    if (!session) {
      log(`Cannot setup socket: no session for ID ${sessionId}`)
      return
    }
    session.socket = ws

    ws.onopen = () => {
      session.connected = true
      session.connected = true
      session.reconnectAttempts = 0
      log(`WS open for session ${sessionId}`)

      // Update activity for all tracked sessions when connection is restored
      updateAllSessionsActivity()
    }

    function parseStateMessage(msg: WsStateMessage) {
      const state = { ...msg.state }
      if (!state.controllers) {
        state.controllers = []
      }
      if (!state.allowed_cmds) {
        state.allowed_cmds = { owner: [], controller: [] }
      }
      if (!state.allowed_cmds.owner) {
        state.allowed_cmds.owner = []
      }
      if (!state.allowed_cmds.controller) {
        state.allowed_cmds.controller = []
      }
      session!.state = state
    }
    function parseErrorMessage(msg: WsErrorMessage) {
      toast.add(`Server error: ${msg.error}`, 'error', `Websocket status: ${ws.readyState}`)
      log(`Server error: ${msg.error}`)
    }
    function parseAckMessage(msg: WsCommandAckMessage) {
      toast.add(
        `${msg.message ?? 'unknown details'}`,
        msg.success ? 'success' : 'error',
        `Command "${msg.cmd}"\nSession ID: ${msg.session_id}`,
      )
      if (!msg.success) {
        log(
          `Command "${msg.cmd}" failed for session ${msg.session_id}: ${msg.message ?? 'unknown error'}`,
        )
      } else {
        log(
          `Command "${msg.cmd}" acknowledged for session ${msg.session_id}: ${msg.message ?? 'no message'}`,
        )
      }
    }

    ws.onmessage = (ev) => {
      log(`<= ${ev.data}`)
      try {
        const msg: WsServerMessage = JSON.parse(ev.data)
        if (msg.type === 'state') {
          parseStateMessage(msg)
        } else if (msg.type === 'error') {
          parseErrorMessage(msg)
        } else if (msg.type === 'ack') {
          parseAckMessage(msg)
        } else {
          toast.add(
            `Unknown message type: ${(msg as { type?: string }).type}`,
            'error',
            JSON.stringify(msg, null, 2),
          )
          log(`Unknown message type: ${(msg as { type?: string }).type}`)
        }
      } catch (e) {
        toast.add(`Error parsing message: ${e}`, 'error', ev.data)
        log(`Parse error: ${e}`)
      }
    }

    ws.onclose = () => {
      session.connected = false
      log('Connection closed.')

      // Mark all sessions as inactive when connection is lost
      updateAllSessionsActivity(false)

      reconnect(sessionId)
    }

    ws.onerror = (ev) => {
      log(`onerror: ${JSON.stringify(ev)}`)
    }
  }

  function hasSocket(sessionId: SessionID): boolean {
    const session = getSession(sessionId)
    return session !== null && session.socket !== null
  }
  function isSocketOpen(sessionId: SessionID): boolean {
    if (!hasSocket(sessionId)) {
      return false
    }
    const session = getSession(sessionId)!
    const isOpen = session.socket!.readyState === WebSocket.OPEN
    const isConnected = session.connected ?? false
    return isOpen && isConnected
  }

  function close(sessionId: SessionID) {
    const session = getSession(sessionId)
    if (session && session.socket) {
      session.socket.close()
      session.socket = null
      session.connected = false
    }
  }

  function connect(sessionId: SessionID) {
    // Close existing connection if session changed
    if (hasSocket(sessionId)) {
      close(sessionId)
    }

    const url = _buildUrl(sessionId)
    if (!url) return
    const ws = new WebSocket(url)
    _setupSocket(ws, sessionId)
  }

  function reconnect(sessionId: SessionID) {
    if (!hasSocket(sessionId)) {
      log(`Socket ${sessionId}: Cannot reconnect: no socket for session.`)
      return
    }
    const session = getSession(sessionId)
    session!.reconnectAttempts += 1
    const backoff = Math.pow(2, session!.reconnectAttempts)
    const delay = Math.min(1000 * backoff, maxReconnectDelay)
    log(`Socket ${sessionId}: Reconnecting in ${delay}ms...`)
    setTimeout(async () => {
      if (!hasSocket(sessionId)) {
        log(`Socket ${sessionId}: Cannot reconnect (after backoff delay): no socket for session.`)
        return
      }
      // The socket may have been dropped because the token ran out, it would be refused again with the same one.
      await auth.ensureFresh()
      if (!hasSocket(sessionId)) return
      const url = _buildUrl(sessionId)
      if (!url) {
        log(`Socket ${sessionId}: Cannot reconnect (after backoff delay): no URL.`)
        return
      }
      const ws = new WebSocket(url)
      _setupSocket(ws, sessionId)
    }, delay)
  }

  function ensureConnected(sessionId: SessionID) {
    if (!isSocketOpen(sessionId)) {
      connect(sessionId)
    }
  }

  // Start tracking activity for a session
  function startActivityTracking(sessionId: SessionID) {
    log(`Starting activity tracking for session ${sessionId}.`)
    ensureConnected(sessionId)
    const session = getSession(sessionId)!

    // Create or update the activity tracker
    session.activityTracker = reactive({
      isActive: false, // Will be updated by updateSessionActivity
      lastActiveTime: Date.now(),
    })

    // Update the activity state immediately
    updateSessionActivity(sessionId)
  }

  // Stop tracking activity for a session
  function stopActivityTracking(sessionId: SessionID) {
    log(`Stopping activity tracking for session ${sessionId}`)
    const session = getSession(sessionId)!

    const tracker = session.activityTracker
    if (tracker && tracker.isActive) {
      // Send final unwatch if currently active
      _sendUnwatchMessage(sessionId)
    }

    session.activityTracker = null
  }

  function sendCmd(sessionId: SessionID, cmd: Command, payload?: Record<string, unknown>) {
    ensureConnected(sessionId)
    if (!isSocketOpen(sessionId)) {
      log(`Cannot send cmd ${cmd}: socket not ready`)
      return
    }
    const msg = { type: 'cmd', session_id: sessionId, cmd, payload }
    log(`=> ${JSON.stringify(msg)}`)
    const socket = getSession(sessionId)!.socket!
    socket.send(JSON.stringify(msg))
  }

  function disconnectAll() {
    for (const sessionId of sessions.value.keys()) {
      disconnect(sessionId)
    }
  }

  function disconnect(sessionId: SessionID) {
    const session = getSession(sessionId)
    const socket = session?.socket
    if (!socket) {
      log('Disconnect: No socket to close.')
      return
    }
    // Send unwatch for all active sessions before closing
    if (session?.activityTracker?.isActive) {
      stopActivityTracking(sessionId)
    }
    close(sessionId)
    log('Disconnected.')
    if (!session) {
      log('Disconnect: No session found, no cleanup needed.')
      return
    }

    // Clear all tracking etc.
    sessions.value.delete(sessionId)
  }

  // Event listeners setup
  function setupActivityListeners() {
    // Window focus/blur events
    const handleFocus = () => {
      isWindowFocused.value = true
      log('Window focused')
      updateAllSessionsActivity()
    }

    const handleBlur = () => {
      isWindowFocused.value = false
      log('Window blurred')
      updateAllSessionsActivity()
    }

    // Online/offline events
    const handleOnline = () => {
      isOnline.value = true
      log('Browser online')
      updateAllSessionsActivity()
    }

    const handleOffline = () => {
      isOnline.value = false
      log('Browser offline')
      updateAllSessionsActivity()
    }

    // Visibility change (mobile sleep detection)
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden
      log(`Document visibility: ${isVisible ? 'visible' : 'hidden'}`)

      if (!isVisible) {
        // Document hidden - treat as inactive
        updateAllSessionsActivity(false)
      } else {
        // Document visible - check normal activity state
        updateAllSessionsActivity()
      }
    }

    // Page freeze/resume events (mobile specific)
    const handleFreeze = () => {
      log('Page frozen')
      updateAllSessionsActivity(false)
    }

    const handleResume = () => {
      log('Page resumed')
      updateAllSessionsActivity()
    }

    // Add event listeners
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    document.addEventListener('freeze', handleFreeze)
    document.addEventListener('resume', handleResume)

    // Page unload cleanup
    const handleBeforeUnload = () => {
      disconnectAll()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Return cleanup function
    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('freeze', handleFreeze)
      document.removeEventListener('resume', handleResume)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }

  // Initialize activity listeners
  const cleanupListeners = setupActivityListeners()

  const useCan = (sessionId: SessionID | null, role: WatchRole | null) => (action: Command) =>
    sessionId !== null && role !== null ? canInSession(getState(sessionId), role, action) : false

  return {
    // State
    watchedSessions,
    debugLog,
    isIdle,
    isWindowFocused,
    isOnline,
    getSession,

    // Core WS management
    startSession,
    stopSession,
    ensureConnected,

    // Core methods
    getState,
    sendCmd,
    disconnect,

    // Commands
    canInCurrentSession: canInSession,
    canInSession,
    useCan,

    // Activity tracking
    startActivityTracking,
    stopActivityTracking,
    updateSessionActivity,
    updateAllSessionsActivity,
    isConnected,
    isUserActive,
    isCountdownConnected,

    // Cleanup
    cleanup: () => {
      cleanupListeners()
    },
  }
})
